import React, { useEffect, useState } from "react";
import {
  Alert,
  BootstrapSizes,
  Box,
  Button,
  ButtonColors,
  Modal,
  Spinner,
  TalentechColors,
} from "@talentech/components";
import ToggleButtonGroup from "@/components/ToggleButtonGroup/ToggleButtonGroup";
import { usePreviewJobMutation } from "@/redux/reducer/directJobs.api";
import type { DirectJobForm } from "@/redux/reducer/directJobs.types";
import { fieldErrorsOf, userMessage } from "./jobFormValidation";

enum PreviewDevice {
  DESKTOP = "desktop",
  MOBILE = "mobile",
}

const DEVICE_WIDTHS: Record<PreviewDevice, string> = {
  [PreviewDevice.DESKTOP]: "100%",
  [PreviewDevice.MOBILE]: "390px",
};

interface JobPreviewModalProps {
  tenantId: string;
  form: DirectJobForm | null;
  onClose: () => void;
}

const JobPreviewModal: React.FC<JobPreviewModalProps> = ({
  tenantId,
  form,
  onClose,
}) => {
  const [device, setDevice] = useState(PreviewDevice.DESKTOP);
  const [previewJob, { data: html, error, isLoading }] =
    usePreviewJobMutation();

  useEffect(() => {
    if (!form?.job_ad_template_id) {
      return;
    }
    const request = previewJob({ tenantId, form });
    return () => request.abort();
  }, [form, tenantId, previewJob]);

  const fieldErrors = fieldErrorsOf(error);

  const content = () => {
    if (!form?.job_ad_template_id) {
      return (
        <Alert
          color={TalentechColors.INFO}
          message="Choose a job ad template to preview the job ad."
        />
      );
    }
    if (isLoading) {
      return <Spinner />;
    }
    if (error) {
      return (
        <Alert
          color={TalentechColors.DANGER}
          message={
            fieldErrors.length
              ? fieldErrors.map(userMessage).join(" ")
              : "Error loading the preview"
          }
        />
      );
    }
    return (
      <Box flex className="justify-content-center bg-gray-100 p-2">
        <iframe
          title="Job ad preview"
          srcDoc={html}
          sandbox="allow-scripts"
          style={{
            width: DEVICE_WIDTHS[device],
            height: "60vh",
            border: 0,
            background: "#fff",
          }}
        />
      </Box>
    );
  };

  return (
    <Modal
      title="Job ad preview"
      showModal={!!form}
      size={BootstrapSizes.XLARGE}
      onClose={onClose}
      buttons={[
        <Button
          key="close"
          label="Close preview"
          color={ButtonColors.PRIMARY}
          onClick={onClose}
        />,
      ]}
    >
      <ToggleButtonGroup
        ariaLabel="Preview device"
        className="mb-3"
        options={[
          { value: PreviewDevice.DESKTOP, label: "Desktop" },
          { value: PreviewDevice.MOBILE, label: "Mobile" },
        ]}
        value={device}
        onChange={setDevice}
      />
      {content()}
    </Modal>
  );
};

export default JobPreviewModal;
